import * as React from 'react';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { DocAccessType, IRisk, Organisation, OrganisationDocList, OrganisationSponsorship } from '../../../../types';
import { OrgType } from 'api-paths-cas';
import CompanySponsorshipLetter from '../../../sections/financeDocs/companySponsorshipLetter';
import { sponsorFundLabel } from '../../../../utilities/sponsors';
import GovSponsorshipLetter from '../../../sections/financeDocs/govSponsorshipLetter';
import { hasFundingRisks, isFundingRisk } from '../../../../utilities/risks';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { UserRole } from '../../../../store/auth/types';
import { SummaryRow } from '../../../Summary/SummaryRow';
import { SummaryGroup } from '../../../Summary/SummaryGroup';

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  id: string;
  documents: Organisation;
  sponsorType: string;
  financials: OrganisationSponsorship;
  risk: IRisk['fundingRisk']['organisationSponsorship'];
  docRisk: IRisk['financeDocsRisk']['organisationSponsorship'];
  userRole?: UserRole;

  canCheckOff?: boolean;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;
  showRisks: boolean;

  isCheckedOffDocRisks?: (docType: string, docKey: string) => boolean;
  isCheckedOffFinanceRisks?: (docType: string, docKey: string) => boolean;
  onCheckOffDocRisks?: (docType: string, docKey: string, docTitle: string) => void;
  onCheckOffFinanceRisks?: (docType: string, docKey: string, docTitle: string) => void;
  onResume: (url: string) => void;
  onUploadClick: (docKey: string) => void;
  onOpenClick: (docKey: string) => void;
  onApproveClick: (docKey: string) => void;
  onRejectClick: (docKey: string) => void;
  onRescindRejectClick: (docKey: string) => void;
  onDeleteClick: (docKey: string) => void;
  onAcknowledgeClick: (docKey: string) => void;
}

const GovernmentOrCompany = (props: IProps) => {
  const { showRisks, infoOnly, userAccess } = props;

  const sponsorAmount = sponsorFundLabel(props.financials);

  const getMetaData = () => {
    return (
      <SummaryGroup
        title={props.financials.orgType}
        key={`organisation_sponsor_${props.id}`}
        boxProps={{ as: 'div', pt: 0 }}
      >
        <SummaryRow
          key={`organisation_type_${props.id}`}
          label={'Organisation type'}
          fullLabel="What type of organisation is sponsoring you?"
          value={props.financials.orgType || ''}
          isRisk={showRisks && isFundingRisk(props.risk, 'orgType', props.id)}
        />

        <SummaryRow
          key={`organisation_sponsor_name_${props.id}`}
          label={'Sponsor name'}
          fullLabel="What is the name of your sponsor or sponsorship?"
          value={props.financials.sponsorName || ''}
          isRisk={showRisks && isFundingRisk(props.risk, 'sponsorName', props.id)}
        />

        {[OrgType.INT_ORG, OrgType.INT_COMP].includes(props.financials.orgType!) ? (
          <SummaryRow
            key={`organisation_sponsor_international_sponsor_${props.id}`}
            label={'Presence in more than one country'}
            fullLabel="Does the Organisation or Company have a presence in more than one country?"
            value={props.financials.isMultiCountry ? 'Yes' : 'No'}
            isRisk={showRisks && isFundingRisk(props.risk, 'isMultiCountry', props.id)}
          />
        ) : (
          <></>
        )}

        <SummaryRow
          key={`organisation_fees_covering_${props.id}`}
          label={'Course fees and living costs covering'}
          fullLabel="Select the costs that your sponsorship can be used towards:"
          value={props.financials.sponsorshipCovers || ''}
          isRisk={showRisks && false}
        />

        <SummaryRow
          key={`organisation_contribution_amount_${props.id}`}
          label={'Contribution amount'}
          value={sponsorAmount}
          isRisk={showRisks && false}
        />
      </SummaryGroup>
    );
  };

  let hasDocs = false;
  let hasDeletedDocs = false;
  const docNodes: React.ReactNode[] = [];

  const { documents: orgDoc, userRole } = props;

  if (orgDoc) {
    const visibleDocs = getVisible({ documentsHash: orgDoc, userAccess, infoOnly, userRole }) as OrganisationDocList[];
    if (hasDeleted(orgDoc)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: OrganisationDocList, index: number) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`${key}-${props.id}`}
              key={`${props.id}-organisationSponsorship-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              isDocumentFixed={item.isDocumentFixed}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={`${props.financials.sponsorName} ${
                Object.keys(orgDoc).length > 1 ? (index > 0 ? index + 1 : '') : ''
              }`}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks
                  ? props.isCheckedOffDocRisks('organisationSponsorship', props.id + key)
                  : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks &&
                props.onCheckOffDocRisks(
                  'organisationSponsorship',
                  props.id + key,
                  `${props.financials.sponsorName} (${
                    props.sponsorType === 'governmentSponsorship' ? 'Government Sponsorship' : 'Company Sponsorship'
                  })`,
                )
              }
              onUploadClick={() => props.onUploadClick(key)}
              onOpenClick={() => props.onOpenClick(key)}
              onApproveClick={() => props.onApproveClick(key)}
              onRejectClick={() => props.onRejectClick(key)}
              onRescindRejectClick={() => props.onRescindRejectClick(key)}
              onDeleteClick={() => props.onDeleteClick(key)}
              onAcknowledgeClick={() => props.onAcknowledgeClick(key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              {item.status !== 'AWAITING' && item.fileNames && item.fileNames.length > 0 ? (
                <>
                  {props.sponsorType === 'governmentSponsorship' ? (
                    <GovSponsorshipLetter
                      id={props.id + key}
                      showRisks={showRisks}
                      risk={props.docRisk}
                      hasOfficialLetterhead={item.hasOfficialLetterhead!}
                      nameAndLogoVisible={item.nameAndLogoVisible!}
                      officiallySigned={item.officiallySigned!}
                      hasDate={item.hasDate!}
                      dateOnLetter={item.dateOnLetter!}
                      selectedTextBodyReq={item.selectedTextBodyReq!}
                      hasContactDetails={item.hasContactDetails!}
                      uploadedBy={item.uploadedBy}
                    />
                  ) : null}

                  {props.sponsorType === 'companySponsorship' ? (
                    <CompanySponsorshipLetter
                      id={props.id + key}
                      showRisks={showRisks}
                      risk={props.docRisk}
                      hasOfficialLetterhead={item.hasOfficialLetterhead!}
                      nameAndLogoVisible={item.nameAndLogoVisible!}
                      officiallySigned={item.officiallySigned!}
                      hasDate={item.hasDate!}
                      dateOnLetter={item.dateOnLetter!}
                      selectedTextBodyReq={item.selectedTextBodyReq!}
                      hasContactDetails={item.hasContactDetails!}
                      uploadedBy={item.uploadedBy}
                    />
                  ) : null}
                </>
              ) : undefined}
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      id={`organization-sponsorship-${props.id}`}
      label={props.financials.sponsorName}
      isRisk={showRisks && hasFundingRisks(props.risk, props.id)}
      isRiskSection={showRisks && hasFundingRisks(props.risk, props.id)}
      canCheckOff={props.canCheckOff}
      isCheckedOff={() =>
        props.isCheckedOffFinanceRisks ? props.isCheckedOffFinanceRisks('organisationSponsorship', props.id) : false
      }
      onCheckOffRisk={() =>
        props.onCheckOffFinanceRisks &&
        props.onCheckOffFinanceRisks('organisationSponsorship', props.id, 'Organisation Sponsorship')
      }
      amount={sponsorAmount}
      canEdit={props.canResume}
      edit={() => {
        props.onResume('finances/step-1');
      }}
      metaData={getMetaData()}
    >
      {hasDocs ? docNodes : <NoDocuments />}
    </Section>
  );
};

export default GovernmentOrCompany;
